import React, { useState, useEffect } from 'react';
import './ShareFFPopup.scss';
import linkIcon from '../../../assets/images/dashboard/header/ap_shared_chain_icon.svg';

import apShareUserEnableIcon from '../../../assets/images/dashboard/sidebar/apShareEnableIcon.svg';
import apShareUserDisableIcon from '../../../assets/images/dashboard/sidebar/apShareDisableIcon.svg';
import apShareUserEditSaveIcon from '../../../assets/images/dashboard/sidebar/apShareUserEditSaveIcon.svg';
import apShareUserEditCancelIcon from '../../../assets/images/dashboard/sidebar/apShareUserEditCancelIcon.svg';

import {
  selectLOIPopupOffset,
  setIsLOISharePopupActive,
  setMenu,
} from '../../../redux/reducers/popupSlice';
import { NONE_MENU_ID } from '../../../common/constants';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';

import {
  selectSharedUsersForActiveLevel,
  shareLOIFFilter,
  removeUserFromSharedLOIFFilter,
  toggleLOIShareState,
  updateLOIFFilterUserShareInfo,
  fetchFFSharedDetailsByUser,
  setUserSharedLOIList,
  setActiveLOIFFShareLevel,
  setSharedDocIds,
  setSharedDocToUserRecord,
  fetchSharedDocsForUser,
  setReceivedDocIds,
  setReceviedDocToUserRecord,
  fetchSharedDocsByUser,
} from '../../../redux/reducers/sidebarSlice';

import classNames from 'classnames';
import { selectDocumentID } from '../../../redux/reducers/documentSlice';
import { selectSelectedDocumentId } from '../../../redux/reducers/dashboardSlice';
import { SharedDetails } from '../../../common/types/SliceTypes';

interface ShareFFPopupProps {
  title: string;
  onClose: () => void;
}

const ShareFFPopup: React.FC<ShareFFPopupProps> = ({ title, onClose }) => {
  const dispatch = useAppDispatch();

  const loiPopupOffset = useAppSelector(selectLOIPopupOffset);
  const filteredDocIds = useAppSelector((state) => state.sidebar.filteredDocIds);
  const loiFFlevel = useAppSelector((state) => state.sidebar.activeLOIFFShareLevel);
  const storedRecipients = useAppSelector(selectSharedUsersForActiveLevel);
  const isSharingEnabled = useAppSelector((state) => state.sidebar.isSharingEnabled);
  const activeLevelUserInfo = useAppSelector((state) =>
    state.sidebar.userSharedLOIList.find((item) => item.value === loiFFlevel),
  );
  const activeDocumentId = useAppSelector(selectSelectedDocumentId);

  // UI State
  const [recipients, setRecipients] = useState<any[]>(storedRecipients || []);
  const [username, setName] = useState('');
  const [userEmailId, setEmail] = useState('');
  const [notify, setNotify] = useState(true);
  const [copied, setCopied] = useState(false);

  // Edit Mode
  const [editMode, setEditMode] = useState(false);
  const [editingEmail, setEditingEmail] = useState<string | null>(null);

  // Validation
  const [errors, setErrors] = useState({
    username: '',
    userEmailId: '',
  });

  // env url
  const webURL = process.env.REACT_APP_API_BASE_URL || 'https://apdt.abstrapoint.com';

  useEffect(() => {
    if (storedRecipients) setRecipients(storedRecipients);
  }, [storedRecipients]);

  const validateInputs = () => {
    const newErrors: any = { username: '', userEmailId: '' };
    let isValid = true;

    if (!username.trim()) {
      newErrors.username = 'Name is required';
      isValid = false;
    }

    if (!userEmailId.trim()) {
      newErrors.userEmailId = 'Email is required';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userEmailId)) {
        newErrors.userEmailId = 'Invalid email format';
        isValid = false;
      }
    }

    // Prevent duplicate
    if (!editMode) {
      if (recipients.some((r) => r.userEmailId.toLowerCase() === userEmailId.toLowerCase())) {
        newErrors.userEmailId = 'Email already added';
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  useEffect(() => {
    loadSharedData();
  }, []);

  // 🚀 SHARE NEW USER
  const handleShare = () => {
    if (!validateInputs()) return;

    const docIdPayload = loiFFlevel === '' ? [activeDocumentId] : filteredDocIds;

    dispatch(
      shareLOIFFilter({
        docId: docIdPayload,
        userEmailId: userEmailId.trim(),
        value: loiFFlevel === '' ? 'inbox' : loiFFlevel,
        nickname: username.trim(),
        notify,
      }),
    ).then((res) => {
      if (res.type.endsWith('fulfilled')) {
        setRecipients([
          ...recipients,
          { username: username.trim(), userEmailId: userEmailId.trim() },
        ]);
        loadSharedData();
        setName('');
        setEmail('');
        setNotify(true);
        setErrors({ username: '', userEmailId: '' });
      }
    }).then(()=> handleDataReload());
  };

  // 🚀 EDIT MODE - CLICK ROW
  const startEdit = (email: string, name: string) => {
    setEditMode(true);
    setEditingEmail(email);
    setName(name);
    setEmail(email);
    setErrors({ username: '', userEmailId: '' });
  };

  const toRecord = (items: SharedDetails[]): Record<string, string[]> =>
    items.reduce((acc, item) => {
      if (!acc[item.name]) acc[item.name] = [];

      item.docs?.forEach((doc) => {
        acc[item.name].push(doc);
      });

      return acc;
    }, {} as Record<string, string[]>);

  function loadSharedData() {
    dispatch(fetchFFSharedDetailsByUser()).then((resp) => {
      if (!resp.payload) {
        console.error('Failed to fetch FF Shared Details');
        return;
      } else {
        const sharedDetails = resp.payload;
        if (sharedDetails?.length === 0) {
          console.warn('No shared FFs found for user');
        } else {
          dispatch(fetchSharedDocsByUser()).then((res) => {
            const data = res.payload as SharedDetails[];
            const docIds = data.flatMap((d) => d.docs);
            dispatch(setSharedDocIds(docIds));
            dispatch(setSharedDocToUserRecord(toRecord(data)));
          });
          dispatch(setUserSharedLOIList(sharedDetails));
        }
      }
    });
  }
  // 🚀 SAVE EDIT
  const handleSaveEdit = () => {
    if (!validateInputs()) return;
    dispatch(
      updateLOIFFilterUserShareInfo({
        emaildId: editingEmail || '',
        newNickname: username.trim(),
        hash: activeLevelUserInfo?.hash || '',
        notify: notify,
      }),
    ).then((res) => {
      if (res.type.endsWith('fulfilled')) {
        loadSharedData();
        setEditMode(false);
        setEditingEmail(null);
        setName('');
        setEmail('');
      }
    });
  };

  // ❌ CANCEL EDIT
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingEmail(null);
    setName('');
    setEmail('');
    setErrors({ username: '', userEmailId: '' });
  };

  // ❌ REMOVE USER
  const removeRecipient = (email: string) => {
    dispatch(
      removeUserFromSharedLOIFFilter({
        userEmailId: email,
        hash: activeLevelUserInfo?.hash || '',
      }),
    ).then((res) => {
      if (res.type.endsWith('fulfilled')) {
        setRecipients(recipients.filter((r) => r.userEmailId !== email));
      }
    });
  };

  // 📌 Copy Link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(webURL + `?hash=${activeLevelUserInfo?.hash}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClose = () => {
    dispatch(setMenu(NONE_MENU_ID));
    dispatch(setIsLOISharePopupActive(false));
    dispatch(setActiveLOIFFShareLevel(''));
    setEditMode(false);
    setEditingEmail(null);
    setName('');
    setEmail('');
    onClose();
  };

  const isShareEnabled = username.trim() !== '' && userEmailId.trim() !== '';

  function handleDataReload(){
    dispatch(fetchSharedDocsByUser()).then(async (res) => {
    const data = res.payload as SharedDetails[];
    const docIds = data.flatMap((d) => d.docs);
    dispatch(setSharedDocIds(docIds));
    dispatch(setSharedDocToUserRecord(toRecord(data)));
    });
                  
    dispatch(fetchSharedDocsForUser()).then(async (res) => {
    const data = res.payload as SharedDetails[];
                  
    const docIds = data.flatMap((d) => d.docs);
    dispatch(setReceivedDocIds(docIds));
                  
    dispatch(setReceviedDocToUserRecord(toRecord(data)));
    });    
  }

  return (
    <div
      className='share-popup-overlay'
      style={{ left: loiPopupOffset[0], top: loiPopupOffset[1] }}>
      <div className='share-popup-card'>
        {/* HEADER */}
        <div className='share-popup-header'>
          <div className='ff-header-row'>
            <div className='ff-share-title'>Share</div>

            {/* TOGGLE BUTTON */}
            <img
              src={isSharingEnabled ? apShareUserEnableIcon : apShareUserDisableIcon}
              className='share-toggle-icon'
              onClick={() =>
                dispatch(
                  toggleLOIShareState({
                    enabled: !isSharingEnabled,
                    hash: activeLevelUserInfo?.hash || '',
                  }),
                ).then(()=> handleDataReload())
              }
            />
          </div>

          <div className='ff-title-holder'>
            <div className='ff-title-text'>{loiFFlevel}</div>

            <div className='ff-tools' onClick={handleCopyLink}>
              {!copied ? (
                <>
                  <span className='copy-link-btn'>Copy Link</span>
                  <button className='close-btn'>
                    <img src={linkIcon} alt='close' />
                  </button>
                </>
              ) : (
                <span className='link-copied'>Link copied</span>
              )}
            </div>
          </div>
        </div>

        {/* ADD / EDIT RECIPIENT */}
        <div className='add-recipient'>
          <label className='section-title'>{editMode ? 'Edit Recipient' : 'Add Recipient'}</label>

          <div className='add-recipient-container'>
            <div className='field-column'>
              <div className='field'>
                <label>Name</label>
                <input
                  disabled={!isSharingEnabled}
                  type='text'
                  placeholder='Enter Name'
                  value={username}
                  onChange={(e) => setName(e.target.value)}
                />
                {errors.username && <div className='error'>{errors.username}</div>}
              </div>

              <div className='field field-email'>
                <label>Email</label>
                <input
                  disabled={!isSharingEnabled || editMode}
                  type='text'
                  placeholder='Enter Email'
                  value={userEmailId}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {errors.userEmailId && <div className='error'>{errors.userEmailId}</div>}
              </div>
            </div>

            <div className='action-column'>
              {/* SHARE vs SAVE/CANCEL */}
              {!editMode ? (
                <div className='save-action-btns'>
                  <label className='notify'>
                    <input
                      type='checkbox'
                      disabled={!isSharingEnabled}
                      checked={notify}
                      onChange={() => setNotify(!notify)}
                    />
                    Notify
                  </label>
                  <button
                    className={classNames('share-btn', {
                      'share-btn-disabled': !isShareEnabled || !isSharingEnabled,
                    })}
                    disabled={!isShareEnabled || !isSharingEnabled}
                    onClick={handleShare}>
                    Share
                  </button>
                </div>
              ) : (
                <div className='edit-action-btns'>
                  <img
                    src={apShareUserEditSaveIcon}
                    className='edit-action-icon'
                    onClick={handleSaveEdit}
                  />

                  <img
                    src={apShareUserEditCancelIcon}
                    className='edit-action-icon'
                    onClick={handleCancelEdit}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* SHARED LIST */}
        <div className='shared-list'>
          <label>Shared with</label>

          <div className='scroll-area'>
            {recipients.length === 0 ? (
              <div className='no-shared'>Not shared</div>
            ) : (
              recipients.map((r) => (
                <div
                  key={r.userEmailId}
                  className={classNames('shared-item', {
                    'editing-row': editingEmail === r.userEmailId,
                  })}
                  onClick={() => startEdit(r.userEmailId, r.username)}>
                  <div className='name'>{r.username}</div>
                  <div className='email'>{r.userEmailId}</div>
                  <button
                    className='remove-btn'
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRecipient(r.userEmailId);
                    }}>
                    x
                  </button>
                </div>
              ))
            )}
          </div>

          <div className='share-popup-footer'>
            <button className='close-btn' onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareFFPopup;
